(() => {
  const controllerSelector = '[data-nwp-headband-controller][data-product-id]';
  const productRootSelector = 'product-rerender[data-nwp-product-controls][id^="product-info-"]';
  const customizeSelector = '#zakeke-product-button';
  const artworkUploadScopeSelector = '[data-nwp-artwork-upload]';
  const studioScopeSelector = '[data-nwp-sleeve-studio], [data-nwp-flag-studio], [data-nwp-apparel-studio]';
  const artworkUploadFieldSelector = '[data-nwp-artwork-upload-field]';
  const mainFormSelector = 'form[id^="product-form-main-"], form#product-main-form';
  const buyBlockSelector = '.product-info__block-item[data-block-type="buy-buttons"]';
  const cartSelector = 'buy-buttons > .button[type="submit"]';
  const stickyCartSelector = '[data-nwp-sticky-cart][type="submit"]';
  const stickyBarSelector = 'nwp-product-sticky-bar[data-nwp-sticky-product-controls]';
  const galleryCarouselSelector = '.product-gallery__carousel[role="region"][tabindex]';
  const zakekeLegacyProxyPath = '/apps/zakeke';
  const zakekeProxyPath = '/apps/nwpcustoms';
  const stallNoticeDelay = 30000;
  const artworkAddTimeout = 60000;
  const artworkReconcileTimeout = 10000;
  const scopeStates = new WeakMap();
  const statusByButton = new WeakMap();
  const stallTimers = new WeakMap();
  const customizerButtons = new Set();
  const activeScopes = new Set();
  let statusSequence = 0;

  const createElement = (name, className) => {
    const element = document.createElement(name);
    element.className = className;
    return element;
  };

  const formId = (form) => (
    form instanceof HTMLFormElement ? form.getAttribute('id') || '' : ''
  );

  const normalizeZakekeProxyAction = (form) => {
    if (!(form instanceof HTMLFormElement)) return;

    try {
      const action = new URL(form.getAttribute('action') || form.action, window.location.href);
      if (action.origin !== window.location.origin || action.pathname !== zakekeLegacyProxyPath) return;
      action.pathname = zakekeProxyPath;
      form.action = action.href;
    } catch {
      return;
    }
  };

  const syncZakekeVariantId = (form) => {
    if (!(form instanceof HTMLFormElement)) return;

    const variantId = new URL(window.location.href).searchParams.get('variant') || '';
    if (!/^\d+$/.test(variantId)) return;

    const variantInput = form.querySelector('input[name="id"]');
    if (!(variantInput instanceof HTMLInputElement)) return;
    variantInput.value = variantId;
    variantInput.disabled = false;
  };

  const restoreZakekeSubmit = (state) => {
    const override = state?.submitOverride;
    if (!override) return;

    if (override.form.submit === override.submit) {
      if (override.descriptor) Object.defineProperty(override.form, 'submit', override.descriptor);
      else delete override.form.submit;
    }
    state.submitOverride = null;
  };

  const installZakekeSubmit = (state, form) => {
    if (!state || !(form instanceof HTMLFormElement)) return;
    if (state.submitOverride?.form === form) return;
    restoreZakekeSubmit(state);

    const originalSubmit = form.submit;
    if (typeof originalSubmit !== 'function') return;
    const descriptor = Object.getOwnPropertyDescriptor(form, 'submit');
    const submit = function submit() {
      normalizeZakekeProxyAction(this);
      return originalSubmit.call(this);
    };

    try {
      Object.defineProperty(form, 'submit', {
        configurable: true,
        value: submit,
        writable: true,
      });
    } catch {
      return;
    }
    state.submitOverride = { descriptor, form, submit };
  };

  const isPrimaryFormId = (id, productId) => (
    id === 'product-main-form' || id.startsWith(`product-form-main-${productId}-`)
  );

  const buttonLabel = (button) => {
    if (!(button instanceof HTMLButtonElement)) return '';
    return (
      button.dataset.nwpFluxLabel
      || button.dataset.nwpFluxCartLabel
      || button.querySelector(':scope > .nwp-flux-content .nwp-flux-label')?.textContent?.trim()
      || button.querySelector(':scope > .nwp-flux-cart-content .nwp-flux-cart-label')?.textContent?.trim()
      || button.textContent.trim()
    );
  };

  const loadingAnnouncement = (button) => {
    const label = buttonLabel(button);
    return label ? `${label}\u2026` : '\u2026';
  };

  const associateStatus = (button, status) => {
    const ids = new Set(
      (button.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && (!id.startsWith('nwp-flux-status-') || document.getElementById(id))),
    );
    ids.add(status.id);
    button.setAttribute('aria-describedby', [...ids].join(' '));
  };

  const getStatus = (kind, button) => {
    const current = statusByButton.get(button);
    if (current?.isConnected) return current;

    const status = createElement('span', 'nwp-flux-status');
    status.id = `nwp-flux-status-${kind}-${++statusSequence}`;
    status.dataset.nwpFluxStatus = kind;
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    button.insertAdjacentElement('afterend', status);
    associateStatus(button, status);
    statusByButton.set(button, status);
    return status;
  };

  const clearStallTimer = (button) => {
    const timer = stallTimers.get(button);
    if (timer) window.clearTimeout(timer);
    stallTimers.delete(button);
  };

  const stalledMessage = (button) => {
    const scope = button.closest(productRootSelector);
    return scopeStates.get(scope)?.controller?.dataset.loadingStalled || '';
  };

  const setCustomizerLoading = (button, loading, statusMessage = '', announce = true) => {
    if (!(button instanceof HTMLButtonElement)) return;
    if (loading && button.dataset.nwpFluxLoading !== 'true') {
      button.setAttribute('data-nwp-flux-loading', 'true');
    } else if (!loading && button.hasAttribute('data-nwp-flux-loading')) {
      button.removeAttribute('data-nwp-flux-loading');
    }
    if (loading && button.getAttribute('aria-busy') !== 'true') {
      button.setAttribute('aria-busy', 'true');
    } else if (!loading && button.hasAttribute('aria-busy')) {
      button.removeAttribute('aria-busy');
    }
    if (!loading) {
      clearStallTimer(button);
      button.removeAttribute('data-nwp-flux-stalled');
    }

    if (announce) {
      const status = getStatus('customizer', button);
      const message = loading ? statusMessage || loadingAnnouncement(button) : '';
      if (status.textContent !== message) status.textContent = message;
    }
  };

  const showCustomizerStallNotice = (button) => {
    stallTimers.delete(button);
    if (button.dataset.nwpFluxLoading !== 'true') return;
    const message = stalledMessage(button);
    if (!message) return;
    button.dataset.nwpFluxStalled = 'true';
    const status = getStatus('customizer', button);
    if (status.textContent !== message) status.textContent = message;
    syncStickyBars();
  };

  const releaseCustomizerButton = (button) => {
    clearStallTimer(button);
    const status = statusByButton.get(button);
    if (status?.isConnected) status.remove();
    statusByButton.delete(button);
    customizerButtons.delete(button);
  };

  const removeSuppressedCustomizers = (scope, buttons) => {
    buttons.forEach((button) => {
      releaseCustomizerButton(button);
      const container = button.closest('.zakeke-product-button-container');
      const otherAction = container instanceof HTMLElement
        ? [...container.querySelectorAll('button, input[type="submit"], a[href]')]
          .some((control) => control !== button && !button.contains(control))
        : false;
      if (container instanceof HTMLElement && scope.contains(container) && !otherAction) {
        container.remove();
      } else {
        button.remove();
      }
    });
  };

  const neutralizeCustomizerContainer = (button) => {
    const container = button.closest('.zakeke-product-button-container');
    if (!(container instanceof HTMLElement) || container.tagName !== 'BUY-BUTTONS') return container;

    const neutralContainer = document.createElement('div');
    [...container.attributes].forEach((attribute) => {
      neutralContainer.setAttribute(attribute.name, attribute.value);
    });
    neutralContainer.append(...container.childNodes);
    container.replaceWith(neutralContainer);
    return neutralContainer;
  };

  const positionCustomizer = (button, buyBlock) => {
    if (!(button instanceof HTMLButtonElement) || !(buyBlock instanceof HTMLElement)) return;
    const customizerBlock = button.closest('.product-info__block-item');
    if (!(customizerBlock instanceof HTMLElement)) return;

    if (customizerBlock === buyBlock) {
      const customizerContainer = neutralizeCustomizerContainer(button);
      const cartStack = buyBlock.querySelector(cartSelector)?.closest('.v-stack');
      if (
        customizerContainer instanceof HTMLElement
        && cartStack instanceof HTMLElement
        && customizerContainer.parentElement === cartStack.parentElement
        && customizerContainer.nextElementSibling !== cartStack
      ) cartStack.insertAdjacentElement('beforebegin', customizerContainer);
      return;
    }

    if (customizerBlock.parentElement !== buyBlock.parentElement) return;
    if (customizerBlock.nextElementSibling === buyBlock) return;
    buyBlock.insertAdjacentElement('beforebegin', customizerBlock);
  };

  const enhanceCustomizer = (button) => {
    if (!(button instanceof HTMLButtonElement)) return;

    // Zakeke injects this button without a type. Set the safe semantic before
    // moving or decorating it so Enter can only submit the native cart button.
    if (button.type !== 'button') button.type = 'button';
    button.classList.remove('nwp-flux-cart');
    if (!button.hasAttribute('data-nwp-headband-customizer')) {
      button.dataset.nwpHeadbandCustomizer = '';
    }
    customizerButtons.add(button);

    if (!button.querySelector(':scope > .nwp-flux-content')) {
      const label = buttonLabel(button);
      button.dataset.nwpFluxLabel = label;

      const content = createElement('span', 'nwp-flux-content');
      const labelElement = createElement('span', 'nwp-flux-label');
      const loader = createElement('span', 'nwp-flux-loader');
      const spinner = createElement('span', 'nwp-flux-spinner');

      labelElement.textContent = label;
      content.append(labelElement);
      loader.setAttribute('aria-hidden', 'true');
      loader.append(spinner);
      button.replaceChildren(content, loader);
      button.dataset.nwpFluxReady = 'true';
    }

    getStatus('customizer', button);
  };

  const enhanceCart = (button, plainLabel = null) => {
    if (!(button instanceof HTMLButtonElement)) return;

    if (!button.dataset.nwpFluxCartDefaultLabel) {
      button.dataset.nwpFluxCartDefaultLabel = buttonLabel(button);
    }
    button.classList.add('nwp-flux-cart');
    if (!button.querySelector(':scope > .nwp-flux-cart-content')) {
      const label = buttonLabel(button);
      button.dataset.nwpFluxCartLabel = label;

      const content = createElement('span', 'nwp-flux-cart-content');
      const labelElement = createElement('span', 'nwp-flux-cart-label');
      const loader = createElement('span', 'nwp-flux-cart-loader');
      const spinner = createElement('span', 'nwp-flux-cart-spinner');

      labelElement.textContent = label;
      content.append(labelElement);
      loader.setAttribute('aria-hidden', 'true');
      loader.append(spinner);
      button.replaceChildren(content, loader);
      button.dataset.nwpFluxCartReady = 'true';
    } else if (!button.dataset.nwpFluxCartLabel) {
      button.dataset.nwpFluxCartLabel = buttonLabel(button);
    }

    if (plainLabel !== null) {
      const label = plainLabel || button.dataset.nwpFluxCartDefaultLabel;
      button.classList.toggle('nwp-plain-purchase', Boolean(plainLabel));
      setMirroredLabel(button, label, '.nwp-flux-cart-label', 'nwpFluxCartLabel');
    }
  };

  const primaryForm = (scope) => {
    const productId = scope.dataset.nwpProductId;
    if (!/^\d+$/.test(productId || '')) return null;
    const observedFormId = scope.getAttribute('observe-form') || '';
    if (!isPrimaryFormId(observedFormId, productId)) return null;
    return [...scope.querySelectorAll(mainFormSelector)].find((form) => (
      formId(form) === observedFormId
    )) || null;
  };

  const handleGalleryKeydown = (scope, event) => {
    if (
      event.defaultPrevented
      || event.altKey
      || event.ctrlKey
      || event.metaKey
      || event.shiftKey
      || !(event.target instanceof HTMLElement)
      || !event.target.matches(galleryCarouselSelector)
      || !scope.contains(event.target)
    ) return;

    const carousel = event.target;
    const isRightToLeft = getComputedStyle(carousel).direction === 'rtl';
    const moveForward = event.key === (isRightToLeft ? 'ArrowLeft' : 'ArrowRight');
    const moveBackward = event.key === (isRightToLeft ? 'ArrowRight' : 'ArrowLeft');
    const currentIndex = Number.isInteger(carousel.selectedIndex) ? carousel.selectedIndex : 0;
    const lastIndex = Array.isArray(carousel.cells) ? carousel.cells.length - 1 : -1;

    if (
      (!moveForward && !moveBackward)
      || (moveForward && currentIndex >= lastIndex)
      || (moveBackward && currentIndex <= 0)
    ) return;

    event.preventDefault();
    if (moveForward) carousel.next?.({ instant: true });
    else carousel.previous?.({ instant: true });
  };

  const mainScopeForStickyBar = (stickyBar) => {
    const sectionRoot = stickyBar.closest('.shopify-section--main-product');
    const targetFormId = stickyBar.getAttribute('form') || '';
    const targetProductId = stickyBar.dataset.nwpProductId || '';
    if (!(sectionRoot instanceof HTMLElement) || !targetFormId || !/^\d+$/.test(targetProductId)) return null;
    const form = [...sectionRoot.querySelectorAll(mainFormSelector)].find((candidate) => (
      formId(candidate) === targetFormId
    ));
    const scope = form?.closest(productRootSelector);
    return scope instanceof HTMLElement
      && sectionRoot.contains(scope)
      && scope.dataset.nwpProductId === targetProductId
      ? scope
      : null;
  };

  const normalFlowBottom = (element) => {
    if (!(element instanceof HTMLElement)) return Number.POSITIVE_INFINITY;
    const stickyAncestors = [];
    let current = element;
    while (current instanceof HTMLElement) {
      if (getComputedStyle(current).position === 'sticky') {
        stickyAncestors.push({
          element: current,
          priority: current.style.getPropertyPriority('position'),
          value: current.style.getPropertyValue('position'),
        });
        current.style.setProperty('position', 'static', 'important');
      }
      if (current.classList.contains('shopify-section--main-product')) break;
      current = current.parentElement;
    }

    try {
      return window.scrollY + element.getBoundingClientRect().bottom;
    } finally {
      stickyAncestors.forEach((entry) => {
        if (entry.value) entry.element.style.setProperty('position', entry.value, entry.priority);
        else entry.element.style.removeProperty('position');
      });
    }
  };

  const updateStickyReservation = () => {
    const bars = [...document.querySelectorAll(stickyBarSelector)].filter((bar) => bar.isConnected);
    const height = bars.reduce((maximum, bar) => (
      Math.max(maximum, Math.ceil(bar.getBoundingClientRect().height))
    ), 0);
    document.documentElement.classList.toggle('nwp-has-product-sticky', bars.length > 0);
    if (height > 0) {
      document.documentElement.style.setProperty('--nwp-product-sticky-reserved-height', `${height}px`);
    } else {
      document.documentElement.style.removeProperty('--nwp-product-sticky-reserved-height');
    }
  };

  const setMirroredLabel = (button, label, labelSelector, dataKey) => {
    if (!(button instanceof HTMLButtonElement) || !label) return;
    if (button.dataset[dataKey] !== label) button.dataset[dataKey] = label;
    const labelElement = button.querySelector(labelSelector);
    if (labelElement && labelElement.textContent !== label) labelElement.textContent = label;
  };

  const mirrorDescription = (button, source) => {
    if (!(button instanceof HTMLButtonElement)) return;
    const ids = source instanceof HTMLButtonElement
      ? (source.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && document.getElementById(id))
      : [];
    const description = ids.join(' ');
    if (description && button.getAttribute('aria-describedby') !== description) {
      button.setAttribute('aria-describedby', description);
    } else if (!description && button.hasAttribute('aria-describedby')) {
      button.removeAttribute('aria-describedby');
    }
  };

  const prepareStickyCustomizer = (button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.type !== 'button') button.type = 'button';

    if (!button.querySelector(':scope > .nwp-flux-content')) {
      const label = buttonLabel(button);
      button.dataset.nwpFluxLabel = label;
      const content = createElement('span', 'nwp-flux-content');
      const labelElement = createElement('span', 'nwp-flux-label');
      const loader = createElement('span', 'nwp-flux-loader');
      const spinner = createElement('span', 'nwp-flux-spinner');
      labelElement.textContent = label;
      content.append(labelElement);
      loader.setAttribute('aria-hidden', 'true');
      loader.append(spinner);
      button.replaceChildren(content, loader);
    } else if (!button.dataset.nwpFluxLabel) {
      button.dataset.nwpFluxLabel = buttonLabel(button);
    }
  };

  const syncStickyBar = (stickyBar) => {
    if (!(stickyBar instanceof HTMLElement)) return;
    const scope = mainScopeForStickyBar(stickyBar);
    const mainCustomizer = scope?.querySelector(`${customizeSelector}[data-nwp-headband-customizer]`);
    const stickyCustomizer = stickyBar.querySelector('[data-nwp-sticky-customizer]');

    if (stickyCustomizer instanceof HTMLButtonElement) {
      prepareStickyCustomizer(stickyCustomizer);
      const hasCustomizer = mainCustomizer instanceof HTMLButtonElement;
      stickyCustomizer.hidden = !hasCustomizer;
      if (hasCustomizer) {
        const label = buttonLabel(mainCustomizer);
        setMirroredLabel(stickyCustomizer, label, '.nwp-flux-label', 'nwpFluxLabel');
        if (stickyCustomizer.disabled !== mainCustomizer.disabled) {
          stickyCustomizer.disabled = mainCustomizer.disabled;
        }
        const loading = mainCustomizer.dataset.nwpFluxLoading === 'true';
        const stalled = loading && mainCustomizer.dataset.nwpFluxStalled === 'true';
        if (stalled && stickyCustomizer.dataset.nwpFluxStalled !== 'true') {
          stickyCustomizer.dataset.nwpFluxStalled = 'true';
        } else if (!stalled && stickyCustomizer.hasAttribute('data-nwp-flux-stalled')) {
          stickyCustomizer.removeAttribute('data-nwp-flux-stalled');
        }
        setCustomizerLoading(
          stickyCustomizer,
          loading,
          stalled ? stalledMessage(mainCustomizer) : '',
          false,
        );
        mirrorDescription(stickyCustomizer, mainCustomizer);
      } else {
        setCustomizerLoading(stickyCustomizer, false, '', false);
        mirrorDescription(stickyCustomizer, null);
      }
    }

    const mainCart = scope?.querySelector(`${buyBlockSelector} ${cartSelector}`);
    const stickyCart = stickyBar.querySelector(stickyCartSelector);
    if (stickyCart instanceof HTMLButtonElement) {
      enhanceCart(stickyCart);
      if (mainCart instanceof HTMLButtonElement) {
        const label = buttonLabel(mainCart);
        setMirroredLabel(stickyCart, label, '.nwp-flux-cart-label', 'nwpFluxCartLabel');
        if (stickyCart.disabled !== mainCart.disabled) stickyCart.disabled = mainCart.disabled;
        const busy = mainCart.getAttribute('aria-busy') === 'true';
        if (busy && stickyCart.getAttribute('aria-busy') !== 'true') {
          stickyCart.setAttribute('aria-busy', 'true');
        } else if (!busy && stickyCart.hasAttribute('aria-busy')) {
          stickyCart.removeAttribute('aria-busy');
        }
        mirrorDescription(stickyCart, mainCart);
      } else {
        mirrorDescription(stickyCart, null);
      }
    }
  };

  const syncStickyBars = (scopeWithInvalidBoundary = null) => {
    document.querySelectorAll(stickyBarSelector).forEach((stickyBar) => {
      syncStickyBar(stickyBar);
      if (mainScopeForStickyBar(stickyBar) === scopeWithInvalidBoundary) {
        stickyBar.invalidateBoundary?.();
      } else {
        stickyBar.scheduleUpdate?.();
      }
    });
  };

  const removeOwnedMarkers = (scope) => {
    scope.querySelectorAll('[data-nwp-headband-buy-buttons]').forEach((element) => {
      element.removeAttribute('data-nwp-headband-buy-buttons');
    });
    scope.querySelectorAll('[data-nwp-headband-customizer]').forEach((button) => {
      releaseCustomizerButton(button);
      button.removeAttribute('data-nwp-headband-customizer');
    });
  };

  const artworkErrorMessage = (scope) => (
    scope.querySelector(artworkUploadFieldSelector)?.dataset.addError
    || 'We could not add this item. Check the artwork file and try again.'
  );

  const artworkToken = (scope) => {
    const field = scope.querySelector(artworkUploadFieldSelector);
    if (!(field instanceof HTMLElement)) return '';
    if (!field.dataset.cartToken) {
      field.dataset.cartToken = window.crypto?.randomUUID?.()
        || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }
    return field.dataset.cartToken;
  };

  const setArtworkBusy = (form, busy) => {
    [...form.elements].forEach((element) => {
      if (!(element instanceof HTMLButtonElement) || element.type !== 'submit') return;
      if (busy) element.setAttribute('aria-busy', 'true');
      else element.removeAttribute('aria-busy');
    });
    syncStickyBars();
  };

  const dispatchArtworkError = (form, message) => {
    form.dispatchEvent(new CustomEvent('cart:error', {
      bubbles: true,
      detail: { error: message },
    }));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  };

  const dispatchArtworkSuccess = (form, responseJson, cartContent, formData) => {
    const items = Object.prototype.hasOwnProperty.call(responseJson, 'items')
      ? responseJson.items
      : [responseJson];
    form.dispatchEvent(new CustomEvent('variant:add', {
      bubbles: true,
      detail: {
        items,
        cart: cartContent,
        onSuccessDo: formData.get('on_success'),
      },
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'variant:add',
        onSuccessDo: formData.get('on_success'),
        cart: cartContent,
      },
    }));
  };

  const cartContainsArtworkToken = (cart, token) => (
    Array.isArray(cart?.items) && cart.items.some((item) => (
      item?.properties?.['_NWP upload token'] === token
    ))
  );

  const parseJson = (text) => {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  };

  const fetchWithTimeout = async (input, init, timeout) => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);
    try {
      return await fetch(input, { ...init, signal: controller.signal });
    } finally {
      window.clearTimeout(timer);
    }
  };

  const handleArtworkSubmit = async (scope, event) => {
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement) || !scope.matches(artworkUploadScopeSelector)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.submitter?.getAttribute('aria-busy') === 'true') return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setArtworkBusy(form, true);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    const token = artworkToken(scope);
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle },
    }));
    const formData = new FormData(form);
    formData.set('properties[_NWP upload token]', token);
    formData.set('sections', sectionsToBundle.join(','));
    const variantId = form.elements.namedItem('id')?.value || '';
    formData.set('sections_url', `${Shopify.routes.root}variants/${variantId}`);

    let responseJson = null;
    try {
      const response = await fetchWithTimeout(`${Shopify.routes.root}cart/add.js`, {
        body: formData,
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }, artworkAddTimeout);
      const responseText = await response.text();
      responseJson = parseJson(responseText);
      if (!response.ok) {
        dispatchArtworkError(form, responseJson?.description || artworkErrorMessage(scope));
        return;
      }

      if (!responseJson) {
        const cartResponse = await fetchWithTimeout(
          `${Shopify.routes.root}cart.js`,
          { cache: 'no-store' },
          artworkReconcileTimeout,
        );
        const cart = cartResponse.ok ? await cartResponse.json() : null;
        if (cartContainsArtworkToken(cart, token)) {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }
        dispatchArtworkError(form, artworkErrorMessage(scope));
        return;
      }

      if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }
      const cartResponse = await fetchWithTimeout(
        `${Shopify.routes.root}cart.js`,
        { cache: 'no-store' },
        artworkReconcileTimeout,
      );
      if (!cartResponse.ok) {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }
      const cartContent = await cartResponse.json();
      cartContent.sections = responseJson.sections || {};
      dispatchArtworkSuccess(form, responseJson, cartContent, formData);
    } catch {
      try {
        const cartResponse = await fetchWithTimeout(
          `${Shopify.routes.root}cart.js`,
          { cache: 'no-store' },
          artworkReconcileTimeout,
        );
        const cartContent = cartResponse.ok ? await cartResponse.json() : null;
        if (cartContainsArtworkToken(cartContent, token)) {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }
      } catch {
        // The add outcome remains unknown; never leave the purchase controls busy.
      }
      dispatchArtworkError(form, artworkErrorMessage(scope));
    } finally {
      setArtworkBusy(form, false);
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  };

  const disposeScope = (scope) => {
    const state = scopeStates.get(scope);
    if (!state) return;
    state.observer.disconnect();
    state.abortController.abort();
    restoreZakekeSubmit(state);
    removeOwnedMarkers(scope);
    customizerButtons.forEach((button) => {
      if (!button.isConnected || button.closest(productRootSelector) === scope) {
        releaseCustomizerButton(button);
      }
    });
    activeScopes.delete(scope);
    scopeStates.delete(scope);
  };

  const enhanceScope = (scope) => {
    const state = scopeStates.get(scope);
    if (!state) return;
    state.scheduled = false;

    const form = primaryForm(scope);
    const buyBlock = form?.closest(buyBlockSelector);
    if (!form || !(buyBlock instanceof HTMLElement) || !scope.contains(buyBlock)) {
      restoreZakekeSubmit(state);
      removeOwnedMarkers(scope);
      syncStickyBars(scope);
      return;
    }

    scope.querySelectorAll('[data-nwp-headband-buy-buttons]').forEach((element) => {
      if (element !== buyBlock) element.removeAttribute('data-nwp-headband-buy-buttons');
    });
    if (!buyBlock.hasAttribute('data-nwp-headband-buy-buttons')) {
      buyBlock.dataset.nwpHeadbandBuyButtons = '';
    }

    let customizers = [...scope.querySelectorAll(customizeSelector)];
    if (scope.matches(artworkUploadScopeSelector) || scope.matches(studioScopeSelector)) {
      removeSuppressedCustomizers(scope, customizers);
      customizers = [];
    }
    if (customizers.length > 0) installZakekeSubmit(state, form);
    else restoreZakekeSubmit(state);
    customizers.forEach((button) => {
      enhanceCustomizer(button);
      positionCustomizer(button, buyBlock);
    });
    buyBlock.querySelectorAll(cartSelector).forEach((button) => {
      const plainLabel = customizers.length > 0
        ? button.closest('buy-buttons')?.dataset.nwpPlainPurchaseLabel || ''
        : '';
      enhanceCart(button, plainLabel);
    });
    customizerButtons.forEach((button) => {
      if (
        !button.isConnected
        || !activeScopes.has(button.closest(productRootSelector))
      ) releaseCustomizerButton(button);
    });
    syncStickyBars(scope);
  };

  const scheduleEnhancement = (scope) => {
    const state = scopeStates.get(scope);
    if (!state || state.scheduled) return;
    state.scheduled = true;
    queueMicrotask(() => enhanceScope(scope));
  };

  const handleCustomizerClick = (scope, event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest(customizeSelector);
    if (
      !(button instanceof HTMLButtonElement)
      || !scope.contains(button)
      || !button.hasAttribute('data-nwp-headband-customizer')
    ) return;

    if (button.dataset.nwpFluxLoading === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (button.disabled) return;
    const form = button.form || button.closest('form');
    if (!(form instanceof HTMLFormElement) || !form.matches(':valid')) return;
    syncZakekeVariantId(form);
    setCustomizerLoading(button, true);
    clearStallTimer(button);
    stallTimers.set(
      button,
      window.setTimeout(() => showCustomizerStallNotice(button), stallNoticeDelay),
    );
    syncStickyBars();
  };

  const initializeController = (controller) => {
    const sectionRoot = controller.closest('.shopify-section--main-product');
    if (!(sectionRoot instanceof HTMLElement)) return;
    const form = [...sectionRoot.querySelectorAll(mainFormSelector)].find((candidate) => {
      const candidateId = formId(candidate);
      const candidateScope = candidate.closest(productRootSelector);
      const currentProductId = candidateScope?.dataset.nwpProductId || '';
      return /^\d+$/.test(currentProductId)
        && isPrimaryFormId(candidateId, currentProductId)
        && candidateScope.getAttribute('observe-form') === candidateId;
    });
    const scope = form?.closest(productRootSelector);
    if (
      !(sectionRoot instanceof HTMLElement)
      || !(scope instanceof HTMLElement)
      || !sectionRoot.contains(scope)
      || scope.getAttribute('observe-form') !== formId(form)
    ) return;
    controller.dataset.productId = scope.dataset.nwpProductId;

    if (scopeStates.has(scope)) {
      scheduleEnhancement(scope);
      return;
    }

    const abortController = new AbortController();
    const observer = new MutationObserver(() => scheduleEnhancement(scope));
    scopeStates.set(scope, {
      abortController,
      controller,
      observer,
      scheduled: false,
      submitOverride: null,
    });
    activeScopes.add(scope);
    scope.addEventListener('click', (event) => handleCustomizerClick(scope, event), {
      capture: true,
      signal: abortController.signal,
    });
    scope.addEventListener('keydown', (event) => handleGalleryKeydown(scope, event), {
      signal: abortController.signal,
    });
    if (scope.matches(artworkUploadScopeSelector)) {
      form.addEventListener('submit', (event) => handleArtworkSubmit(scope, event), {
        capture: true,
        signal: abortController.signal,
      });
    }
    form.addEventListener('product:rerender', (event) => {
      if (!event.detail?.productChange) return;
      queueMicrotask(() => {
        disposeScope(scope);
        initializeControllers();
      });
    }, { signal: abortController.signal });
    observer.observe(scope, {
      attributes: true,
      attributeFilter: ['aria-busy', 'disabled', 'type', 'data-nwp-flux-loading'],
      childList: true,
      subtree: true,
    });
    enhanceScope(scope);
  };

  const initializeControllers = () => {
    document.querySelectorAll(controllerSelector).forEach(initializeController);
    syncStickyBars();
  };

  class NwpProductStickyBar extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      this.frame = 0;
      this.observedBuyBlock = null;
      this.observedScope = null;
      this.boundary = Number.POSITIVE_INFINITY;
      this.boundaryDirty = true;
      this.layoutResizeObserver = new ResizeObserver(() => this.invalidateBoundary());
      this.barResizeObserver = new ResizeObserver(() => {
        updateStickyReservation();
      });
      this.barResizeObserver.observe(this);
      this.mutationObserver = new MutationObserver(() => this.scheduleUpdate());
      this.mutationObserver.observe(this, {
        attributes: true,
        attributeFilter: ['aria-busy', 'disabled'],
        childList: true,
        subtree: true,
      });
      window.addEventListener('scroll', () => this.scheduleUpdate(), {
        passive: true,
        signal: this.abortController.signal,
      });
      window.addEventListener('resize', () => this.invalidateBoundary(), {
        passive: true,
        signal: this.abortController.signal,
      });
      const scope = mainScopeForStickyBar(this);
      const form = scope ? primaryForm(scope) : null;
      form?.addEventListener('product:rerender', () => this.invalidateBoundary(), {
        signal: this.abortController.signal,
      });
      this.addEventListener('click', (event) => this.handleClick(event), {
        signal: this.abortController.signal,
      });
      this.scheduleUpdate();
      updateStickyReservation();
    }

    disconnectedCallback() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.layoutResizeObserver?.disconnect();
      this.barResizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.abortController?.abort();
      this.abortController = null;
      queueMicrotask(updateStickyReservation);
    }

    mainBuyBlock() {
      const scope = mainScopeForStickyBar(this);
      const form = scope ? primaryForm(scope) : null;
      return form?.closest(buyBlockSelector) || null;
    }

    scheduleUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    invalidateBoundary() {
      this.boundaryDirty = true;
      this.scheduleUpdate();
    }

    update() {
      const scope = mainScopeForStickyBar(this);
      const buyBlock = this.mainBuyBlock();
      if (buyBlock !== this.observedBuyBlock || scope !== this.observedScope) {
        this.layoutResizeObserver.disconnect();
        this.observedBuyBlock = buyBlock;
        this.observedScope = scope;
        this.boundaryDirty = true;
        if (document.body instanceof HTMLElement) this.layoutResizeObserver.observe(document.body);
        if (scope instanceof HTMLElement) this.layoutResizeObserver.observe(scope);
        if (buyBlock instanceof HTMLElement) this.layoutResizeObserver.observe(buyBlock);
      }

      if (this.boundaryDirty) {
        this.boundary = normalFlowBottom(buyBlock);
        this.boundaryDirty = false;
      }

      const stickyOffset = Number.parseFloat(
        getComputedStyle(document.documentElement).scrollPaddingTop,
      ) || 0;
      const visible = buyBlock instanceof HTMLElement
        && window.scrollY + stickyOffset >= this.boundary;
      if (this.classList.contains('is-visible') !== visible) {
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
        this.inert = !visible;
      }
      syncStickyBar(this);
    }

    handleClick(event) {
      if (!(event.target instanceof Element)) return;
      const proxy = event.target.closest('[data-nwp-sticky-customizer]');
      if (!(proxy instanceof HTMLButtonElement) || !this.contains(proxy)) return;
      event.preventDefault();

      const mainCustomizer = mainScopeForStickyBar(this)
        ?.querySelector(`${customizeSelector}[data-nwp-headband-customizer]`);
      if (!(mainCustomizer instanceof HTMLButtonElement) || mainCustomizer.disabled) return;
      mainCustomizer.click();
      syncStickyBar(this);
    }
  }

  if (!customElements.get('nwp-product-sticky-bar')) {
    customElements.define('nwp-product-sticky-bar', NwpProductStickyBar);
  }

  window.addEventListener('pagehide', () => {
    customizerButtons.forEach(clearStallTimer);
  });
  window.addEventListener('pageshow', () => {
    customizerButtons.forEach((button) => {
      if (button.isConnected) setCustomizerLoading(button, false);
      else releaseCustomizerButton(button);
    });
    activeScopes.forEach(scheduleEnhancement);
    syncStickyBars();
  });
  document.addEventListener('shopify:section:load', initializeControllers);
  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element)) return;
    activeScopes.forEach((scope) => {
      const state = scopeStates.get(scope);
      if (
        event.target.contains(scope)
        || (state?.controller instanceof Element && event.target.contains(state.controller))
      ) disposeScope(scope);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeControllers, { once: true });
  } else {
    initializeControllers();
  }
})();
